import {
  CountryCode,
  useShopSettingsUpdateMutation,
  useSiteSettingsQuery,
} from "@dashboard/graphql";
import useNotifier from "@dashboard/hooks/useNotifier";
import useShop from "@dashboard/hooks/useShop";
import { findInEnum } from "@dashboard/misc";
import { zodResolver } from "@hookform/resolvers/zod";
import React from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";

import { StepWrapper } from "../../components/StepWrapper";
import { useWizardContext } from "../../context/WizardContext";
import {
  COMPANY_INFORMATION_FORM_ID,
  CompanyInformationForm,
} from "./CompanyInformationForm";
import { messages } from "./messages";
import { companyInformationSchema } from "./schema";
import { CompanyInformationFormValues } from "./types";

export const CompanyInformationStep: React.FC = () => {
  const intl = useIntl();
  const notify = useNotifier();
  const shop = useShop();
  const { completeCurrentStep, goToNextStep } = useWizardContext();

  const { data: siteSettingsData } = useSiteSettingsQuery({
    displayLoader: true,
  });

  const [updateShopSettings, { loading }] = useShopSettingsUpdateMutation({
    onCompleted: data => {
      const errors = [
        ...(data?.shopSettingsUpdate?.errors || []),
        ...(data?.shopAddressUpdate?.errors || []),
      ];

      if (errors.length === 0) {
        notify({ status: "success", text: intl.formatMessage(messages.successMessage) });
        completeCurrentStep();
        goToNextStep();
      } else {
        notify({
          status: "error",
          text: errors[0]?.message ?? intl.formatMessage(messages.errorMessage),
        });
      }
    },
  });

  const shopData = siteSettingsData?.shop;

  const formValues: CompanyInformationFormValues = {
    companyName: shopData?.companyAddress?.companyName ?? "",
    description: shopData?.description ?? "",
    streetAddress1: shopData?.companyAddress?.streetAddress1 ?? "",
    streetAddress2: shopData?.companyAddress?.streetAddress2 ?? "",
    city: shopData?.companyAddress?.city ?? "",
    postalCode: shopData?.companyAddress?.postalCode ?? "",
    countryArea: shopData?.companyAddress?.countryArea ?? "",
    country: shopData?.companyAddress?.country?.code ?? "",
    phone: shopData?.companyAddress?.phone ?? "",
  };

  const { control, handleSubmit } = useForm<CompanyInformationFormValues>({
    resolver: zodResolver(companyInformationSchema),
    defaultValues: formValues,
    values: shopData ? formValues : undefined,
  });

  const onSubmit = async (data: CompanyInformationFormValues) => {
    await updateShopSettings({
      variables: {
        shopSettingsInput: {
          description: data.description,
        },
        addressInput: {
          companyName: data.companyName,
          streetAddress1: data.streetAddress1,
          streetAddress2: data.streetAddress2,
          city: data.city,
          postalCode: data.postalCode,
          country: findInEnum(data.country, CountryCode),
          countryArea: data.countryArea,
          phone: data.phone,
        },
      },
    });
  };

  const countryChoices = (shop?.countries ?? []).map(c => ({
    label: c.country,
    value: c.code,
  }));

  return (
    <StepWrapper
      title={intl.formatMessage(messages.title)}
      description={intl.formatMessage(messages.description)}
      skippable={false}
      formId={COMPANY_INFORMATION_FORM_ID}
      isSaving={loading}
    >
      <CompanyInformationForm
        control={control}
        countryChoices={countryChoices}
        disabled={loading}
        onSubmit={handleSubmit(onSubmit)}
      />
    </StepWrapper>
  );
};
